import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { api, ExportResponse } from '@/lib/api-client';
import { downloadFile } from '@/lib/download';

interface FormData {
  id: string;
  name: string;
  description: string;
}

interface Message {
  id: string;
  form_id: string;
  timestamp: number;
  form_data: Record<string, unknown>;
  source_ip: string;
  user_agent?: string;
  spam_score?: number;
  processing_status: 'pending' | 'sent' | 'failed';
  telegram_message_ids?: Record<string, number>;
}

interface MessagesResponse {
  messages: Message[];
  count: number;
  limit: number;
}

export function FormMessagesPage() {
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<FormData | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [exportPeriod, setExportPeriod] = useState('7d');
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [isExporting, setIsExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const { toast } = useToast();

  const limit = 50;

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    if (!id) return;

    try {
      const [formData, messagesData] = await Promise.all([
        api.getForm(id),
        api.getFormMessages(id, { limit: String(limit) }),
      ]);

      setForm(formData as FormData);
      const response = messagesData as MessagesResponse;
      setMessages(response.messages || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load messages',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadMore = async () => {
    if (!id || messages.length === 0) return;

    setIsLoadingMore(true);
    try {
      const lastMessage = messages[messages.length - 1];
      const endTime = new Date(lastMessage.timestamp * 1000).toISOString();

      const messagesData = await api.getFormMessages(id, {
        limit: String(limit),
        end_time: endTime,
      });

      const response = messagesData as MessagesResponse;
      const newMessages = response.messages || [];

      // Filter out any duplicates
      const existingIds = new Set(messages.map(m => m.id));
      const uniqueNew = newMessages.filter(m => !existingIds.has(m.id));

      setMessages([...messages, ...uniqueNew]);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load more messages',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleRefresh = () => {
    setIsLoading(true);
    setMessages([]);
    setExpandedRows(new Set());
    loadData();
  };

  const calculateDateRange = (period: string) => {
    const now = new Date();
    let startTime: string | undefined;

    if (period !== 'all') {
      const hoursMap: Record<string, number> = { '24h': 24, '7d': 168, '30d': 720, '90d': 2160 };
      const hours = hoursMap[period] || 168;
      const start = new Date(now.getTime() - hours * 60 * 60 * 1000);
      startTime = start.toISOString();
    }

    return { startTime, endTime: now.toISOString() };
  };

  const handleExport = async () => {
    if (!id) return;
    setIsExporting(true);
    try {
      const { startTime, endTime } = calculateDateRange(exportPeriod);
      const result = await api.exportMessages(id, startTime, endTime, exportFormat) as ExportResponse;

      const safeName = (form?.name || 'submissions').replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `${safeName}-${exportPeriod}-${dateStr}.${exportFormat}`;

      const data = exportFormat === 'csv'
        ? result.data as string
        : JSON.stringify(result.data, null, 2);
      const mimeType = exportFormat === 'csv' ? 'text/csv;charset=utf-8;' : 'application/json';

      downloadFile(data, filename, mimeType);
      toast({ title: 'Success', description: `Exported ${result.count} submissions` });
      setExportOpen(false);
    } catch {
      toast({ title: 'Error', description: 'Export failed', variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  const toggleRow = (messageId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  const copyToClipboard = (data: Record<string, unknown>) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast({
      title: 'Copied',
      description: 'Form data copied to clipboard',
    });
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      sent: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
    };
    const labels = {
      sent: 'Sent',
      pending: 'Pending',
      failed: 'Failed',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status as keyof typeof labels] || status}
      </span>
    );
  };

  const getFormDataPreview = (formData: Record<string, unknown>) => {
    const entries = Object.entries(formData);
    const preview = entries.slice(0, 3).map(([key, value]) => {
      const strValue = typeof value === 'string' ? value : JSON.stringify(value);
      const truncated = strValue.length > 30 ? strValue.slice(0, 30) + '...' : strValue;
      return `${key}: ${truncated}`;
    });
    return preview.join(', ') + (entries.length > 3 ? ` (+${entries.length - 3} more)` : '');
  };

  const getTelegramDeliveryInfo = (telegramMessageIds?: Record<string, number>) => {
    if (!telegramMessageIds) return null;
    const count = Object.keys(telegramMessageIds).length;
    return count > 0 ? `Delivered to ${count} chat${count > 1 ? 's' : ''}` : null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Form not found</p>
      </div>
    );
  }

  return (
    <AppLayout>
      <div className="mb-8">
          <Link to={`/app/forms/${id}`} className="text-blue-600 hover:underline mb-2 inline-block">
            ← Back to Form
          </Link>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Message History</h1>
              <p className="text-gray-600">{form.name}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
                Refresh
              </Button>
              <Popover open={exportOpen} onOpenChange={setExportOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline">
                    Export
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-64">
                  <div className="space-y-4">
                    <h4 className="font-medium">Export Submissions</h4>

                    <div className="space-y-2">
                      <label className="text-sm text-gray-600">Period</label>
                      <div className="flex flex-wrap gap-1">
                        {['24h', '7d', '30d', '90d', 'all'].map((p) => (
                          <Button
                            key={p}
                            variant={exportPeriod === p ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setExportPeriod(p)}
                            className="text-xs"
                          >
                            {p === 'all' ? 'All' : p.toUpperCase()}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm text-gray-600">Format</label>
                      <Select value={exportFormat} onValueChange={(v) => setExportFormat(v as 'csv' | 'json')}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="csv">CSV</SelectItem>
                          <SelectItem value="json">JSON</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      onClick={handleExport}
                      disabled={isExporting}
                      className="w-full"
                    >
                      {isExporting ? 'Exporting...' : 'Download'}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Submissions</CardTitle>
            <CardDescription>
              {totalCount > 0
                ? `Showing ${messages.length} of ${totalCount} submissions`
                : 'No submissions yet'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <p className="text-gray-500 py-8 text-center">
                No form submissions yet. Once someone submits your form, messages will appear here.
              </p>
            ) : (
              <div className="space-y-0">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-100 rounded-t-lg text-sm font-medium text-gray-600">
                  <div className="col-span-3">Timestamp</div>
                  <div className="col-span-5">Form Data</div>
                  <div className="col-span-2">Source IP</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-1"></div>
                </div>

                {/* Table Rows */}
                {messages.map((message) => (
                  <div key={message.id} className="border-b last:border-b-0">
                    {/* Main Row */}
                    <div
                      className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer items-center"
                      onClick={() => toggleRow(message.id)}
                    >
                      <div className="col-span-3 text-sm">
                        {formatTimestamp(message.timestamp)}
                      </div>
                      <div className="col-span-5 text-sm text-gray-600 truncate">
                        {getFormDataPreview(message.form_data)}
                      </div>
                      <div className="col-span-2 text-sm font-mono text-gray-500">
                        {message.source_ip}
                      </div>
                      <div className="col-span-1">
                        {getStatusBadge(message.processing_status)}
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(message.form_data);
                          }}
                          title="Copy form data"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Row */}
                    {expandedRows.has(message.id) && (
                      <div className="px-4 py-4 bg-gray-50 border-t">
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Form Data</h4>
                            <pre className="bg-white p-3 rounded border text-xs overflow-x-auto">
                              {JSON.stringify(message.form_data, null, 2)}
                            </pre>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            {message.user_agent && (
                              <div>
                                <span className="text-gray-500">User Agent:</span>
                                <p className="text-gray-700 truncate" title={message.user_agent}>
                                  {message.user_agent}
                                </p>
                              </div>
                            )}

                            {message.spam_score !== undefined && message.spam_score > 0 && (
                              <div>
                                <span className="text-gray-500">Spam Score:</span>
                                <p className="text-gray-700">{(message.spam_score * 100).toFixed(1)}%</p>
                              </div>
                            )}

                            {getTelegramDeliveryInfo(message.telegram_message_ids) && (
                              <div>
                                <span className="text-gray-500">Telegram:</span>
                                <p className="text-green-600">
                                  {getTelegramDeliveryInfo(message.telegram_message_ids)}
                                </p>
                              </div>
                            )}

                            <div>
                              <span className="text-gray-500">Submission ID:</span>
                              <p className="text-gray-700 font-mono text-xs">{message.id}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Load More Button */}
                {messages.length < totalCount && (
                  <div className="py-4 text-center">
                    <Button
                      variant="outline"
                      onClick={loadMore}
                      disabled={isLoadingMore}
                    >
                      {isLoadingMore ? 'Loading...' : `Load More (${totalCount - messages.length} remaining)`}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
    </AppLayout>
  );
}
